import { Controller } from "@hotwired/stimulus"
import { computePosition, flip, shift, limitShift, offset, size, autoUpdate } from "@floating-ui/dom"
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import * as Log from "web-ui/log"

// Connects to data-controller="popover"
export default class extends Controller {
  static targets = ["button", "popover"]

  connect() {
    this.debug("Connect")
    this.popoverTarget.addEventListener("toggle", this.handleToggle)
  }

  disconnect() {
    this.debug("Disconnect")
    if (this.hasPopoverTarget) {
      this.popoverTarget.removeEventListener("toggle", this.handleToggle)
    }
    this.stopAutoUpdate()
    this.stopIntersectionObserver()
  }

  handleToggle = (event) => {
    if (event.newState === "open") {
      this.onOpen()
    } else {
      this.onClose()
    }
  }

  onOpen() {
    morphdomIgnore(this.element, true)
    this.popoverTarget.scrollTop = 0
    this.popoverTarget.style.left = null
    this.popoverTarget.style.top = null

    const scrollIndicator = this.popoverTarget.querySelector("scroll-indicator")
    scrollIndicator.displayIndicators()

    this.startIntersectionObserver()

    // Wait for the popover to render before computing position - Aaron, Fri Jan 23 2026
    requestAnimationFrame(() => {
      this.disableAutoUpdate = autoUpdate(
        this.buttonTarget,
        this.popoverTarget,
        this.updatePosition
      )
    })
  }

  onClose() {
    morphdomIgnore(this.element, false)
    this.popoverTarget.classList.remove("top", "bottom")
    this.stopAutoUpdate()
    this.stopIntersectionObserver()
  }

  stopAutoUpdate() {
    if (this.disableAutoUpdate) {
      this.disableAutoUpdate()
      this.disableAutoUpdate = null
    }
  }

  startIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting) {
          this.popoverTarget.hidePopover()
        }
      },
      { threshold: 0 }
    )
    this.intersectionObserver.observe(this.buttonTarget)
  }

  stopIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }

  updatePosition = () => {
    computePosition(this.buttonTarget, this.popoverTarget, {
      placement: "top",
      strategy: "fixed",
      middleware: [
        offset(8),
        shift({ padding: 16, limiter: limitShift({ offset: 32 }) }),
        flip({ padding: 16 }),
        size({
          padding: 16,
          apply: ({ availableHeight, elements }) => {
            elements.floating.style.maxHeight = `${availableHeight}px`
          },
        }),
      ],
    }).then(({ x, y, placement }) => {
      if (placement === "top") {
        this.popoverTarget.classList.add("top")
        this.popoverTarget.classList.remove("bottom")
      } else {
        this.popoverTarget.classList.add("bottom")
        this.popoverTarget.classList.remove("top")
      }

      // Calculate arrow position relative to popover (center of button)
      const buttonRect = this.buttonTarget.getBoundingClientRect()
      const buttonCenterX = buttonRect.left + buttonRect.width / 2
      // Subtract 1px to account for border/transform rounding
      const arrowX = buttonCenterX - x - 1

      this.popoverTarget.style.left = `${x}px`
      this.popoverTarget.style.top = `${y}px`
      this.popoverTarget.style.setProperty("--popover-arrow-x", `${arrowX}px`)
    })
  }

  debug(message, data) {
    Log.debug("Popover - " + message, data)
  }
}
